import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import { useState } from "react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import ContactModal from "@/components/ContactModal";

const SECTIONS = [
  {
    title: "The service",
    body: [
      "HighRank is a public discovery leaderboard for startups and businesses. It has two core experiences: the Leaderboard (ranked listings with two separate signals) and Discover (a curated, media-rich grid of hand-picked featured businesses).",
      "Every leaderboard listing shows two separate signals. Rank Position includes a global Top 3 manually assigned by HighRank from Claimed + Verified businesses; below that, companies appear in order of platform activity. Boost controls Top Spot placement only and never changes leaderboard order or the HighRank Score (0–100), which is based on average star rating (40%), written review volume (20%), endorsements (20%) and profile completeness (20%).",
      "The HighRank Score is HighRank's own platform score. It is not a Google rating, not an industry certification, not a guarantee of quality, and it is never influenced by payments.",
      "Listing and claiming a business is free during the launch phase.",
    ],
  },
  {
    title: "Reviews and endorsements",
    body: [
      "Star ratings and written feedback require a signed-in account and are limited to one review per person per company.",
      "Endorsements (thumbs up) require a signed-in account and are limited to one per person per company.",
      "Only review or endorse businesses you genuinely know. Fake, incentivised or self-directed reputation signals may be removed and accounts suspended.",
      "Companies with no reviews or endorsements display 'Not yet scored' — we never show a fabricated 0/100.",
    ],
  },
  {
    title: "Listings and claims",
    body: [
      "HighRank may publish basic listings for companies that have not joined the platform. An 'Unclaimed' badge marks these listings; being listed implies no endorsement of, or partnership with, HighRank.",
      "Claiming a listing requires verification of your role at the business. We may approve or reject claims at our discretion and will email the decision to your business address.",
      "Any business may request correction or removal of its listing by emailing support@highrank.space.",
    ],
  },
  {
    title: "Discover and featured content",
    body: [
      "The Discover tab is an editorially curated grid of featured businesses. Appearing on Discover is not guaranteed and is subject to HighRank's editorial discretion.",
      "Businesses may submit a feature request (including business name, category, a short message and preferred boost type) via the 'Get Featured' form. Submissions are reviewed by an admin before any post is published.",
      "Approved Discover posts may include video previews, images or Instagram links provided by the business. These are displayed publicly including to unauthenticated visitors.",
      "Visibility upgrades (Discover Boost, Boost, Pro Boost) are optional paid features that provide additional placement and exposure on the platform. Current pricing is displayed on the Top Spot page.",
      "HighRank may remove a Discover post at any time if content violates these terms or if the feature period has ended.",
      "Featured posts are not endorsements of the businesses shown and HighRank takes no responsibility for the accuracy of business-supplied content.",
    ],
  },
  {
    title: "Media uploads and content standards",
    body: [
      "Verified or Claimed business owners may upload thumbnail images and promotional videos to their profile and, if featured, to their Discover card.",
      "All uploaded media is reviewed by a HighRank admin before it is made publicly visible. Media that does not meet HighRank's content standards will be rejected.",
      "Content standards require that all images, thumbnails and videos: (a) are directly relevant to the business being promoted, (b) are professional in quality and not misleading, (c) do not contain NSFW, violent, hateful or offensive material, (d) do not infringe third-party intellectual property rights, and (e) are not spam, duplicates, stock filler or unrelated promotional material.",
      "Videos must showcase the actual product, service or team behind the listing. Generic, recycled or irrelevant footage will be rejected.",
      "If submitted media is rejected for not meeting these standards, the associated Boost payment is eligible for a full refund. Refunds are processed back to the original payment method via Stripe within 5–10 business days.",
      "HighRank reserves the right to reject any media at its sole discretion, even if the content does not explicitly violate a listed rule, if it is deemed not suitable for the platform's audience.",
      "By uploading media you confirm you hold the necessary rights to use it on the platform and grant HighRank a non-exclusive licence to display and serve it as part of your listing.",
      "Media files are stored securely and served over CDN. Files are retained while your listing is active and deleted on request.",
      "HighRank may generate a branded share image (1200×630 PNG) from any public Discover post using the post's business name, tagline, category and media thumbnail. This image may be shared publicly and is not stored permanently.",
    ],
  },
  {
    title: "Boost and payments",
    body: [
      "Boost is a paid visibility feature that controls Top Spot placement on the leaderboard. It is separate from the HighRank Score and has no effect on a company's reputation rating.",
      "Payments are processed by Stripe. By activating a Boost you agree to Stripe's Terms of Service. HighRank does not store your payment card details.",
      "Boost pricing tiers: Discover Boost ($4.99/7 days), Boost ($9.99/10 days), Pro Boost ($14.99/10 days). Pricing may be updated; the confirmed price is always shown at checkout.",
      "Refund policy: If your submitted content (images, videos, links) is rejected by HighRank during the admin review process, a full refund will be initiated automatically. No refunds are issued once content has been approved and the Boost is live, except at HighRank's discretion.",
      "Sponsored visibility tiers (Discover Boost, Boost, Pro Boost) may be allocated based on availability. Sponsored tags are always visible on boosted listings.",
      "Replacing a live video on an active Boost requires a new Boost purchase. Free replacement is not available once content is approved and live.",
      "HighRank reserves the right to suspend a Boost if the associated business listing violates these terms.",
    ],
  },
  {
    title: "Acceptable use",
    body: [
      "Don't scrape the platform at abusive rates, attempt to manipulate Rank Positions or HighRank Scores, or post unlawful, defamatory or misleading content.",
      "Don't upload media you don't own or that infringes third-party intellectual property rights.",
      "Don't submit false or fabricated claim requests or feature request submissions.",
      "Don't submit spam, irrelevant or repetitive content through the Boost or Discover submission flows.",
      "Repeated violations of content standards (e.g., submitting rejected media multiple times) may result in account suspension and forfeiture of any active Boost.",
      "We may remove content or suspend accounts that break these rules without prior notice.",
    ],
  },
  {
    title: "Liability",
    body: [
      "The leaderboard, scores and Discover content are provided 'as is' for discovery purposes. Decisions you make based on listings are your own.",
      "Share images generated from public post data are provided as-is for convenience. HighRank does not guarantee that external platforms (Twitter, WhatsApp, etc.) will render them correctly.",
      "To the maximum extent permitted by law, HighRank is not liable for indirect or consequential losses arising from use of the platform.",
    ],
  },
  {
    title: "Changes",
    body: [
      "We may update these terms as the product evolves. Material changes will be noted on this page with a revised date.",
    ],
  },
];

export default function Terms() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <main data-testid="terms-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title="Terms of Service — HighRank"
        siteName="HighRank"
        description="The rules of the board: how Rank Position, Boost and the HighRank Score work, review rules, listing claims and acceptable use."
      />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <FadeUp>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-400 transition-colors mb-8"
          >
            <ArrowLeft size={13} /> Home
          </Link>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-3">{"// Legal"}</p>
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wide text-slate-100 mb-3">
            Terms of Service
          </h1>
          <p className="text-sm text-slate-500 mb-12 flex items-center gap-2">
            <ScrollText size={14} className="text-emerald-400" /> Last updated: September 2026
          </p>
        </FadeUp>
        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.04}>
              <section>
                <h2 className="font-heading text-lg font-bold text-slate-100 mb-3">{s.title}</h2>
                <ul className="space-y-2.5">
                  {s.body.map((line, j) => (
                    <li key={j} className="text-sm text-slate-400 leading-relaxed flex gap-3">
                      <span className="text-emerald-500 mt-0.5 shrink-0">—</span>
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.1}>
          <div className="mt-14 rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="text-sm text-slate-400 leading-relaxed flex-1">
              Questions about these terms or anything else on HighRank?
            </p>
            <button
              data-testid="terms-contact-btn"
              onClick={() => setContactOpen(true)}
              className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all hover:opacity-90"
              style={{ backgroundColor: "#3FE08C", color: "#000" }}
            >
              Contact us
            </button>
          </div>
          <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
        </FadeUp>
      </section>
    </main>
  );
}
